import type { ArchitecturalMetricsSnapshot } from "src/types/ArchitecturalMetrics";
import { applyBki } from "src/service/cost/utility/apply-bki";
import type {
  BkiCity,
  BkiSizeBracket,
  BkiStandard,
} from "src/service/cost/data/bki";

// Hard-coded German DIN 276 cost engine for the cost-engine-compare screen.
// Pure functions — extracted from the screen so unit tests can exercise the
// math without spinning up React.
//
// Spec: docs/cost-structure-din276.md (Part D — input contract).
//
// BKI integration: pass an optional `bki` param to fill rate 200/300/400/500
// (and kg600 as kg600€/m² × GLA) from the BKI matrix. Source labels become
// "BKI · <city>" so the UI can show provenance.

export type BkiInput = {
  city: BkiCity;
  sizeBracket?: BkiSizeBracket;
  standard?: BkiStandard;
};

// Reference-area basis for the KG 300/400 construction lines.
//   • "bgf" — BGF / GEA (gross floor area), the basis BKI rates are actually
//             quoted against; the intended default for German costing.
//   • "strict" / "nia" / "leasable" — pragmatic Wfl (Wohnfläche) derivations,
//             kept as selectable fallbacks in case we revert.
// (Type name kept as WflOption to match the screen's existing state names;
// renamed alongside the UI toggle.)
export type WflOption = "bgf" | "strict" | "nia" | "leasable";

export type Resolved<T> = { value: T; source: string };

export type KgResult = {
  value: number | null;
  formula: string;
  missing: string[]; // names of inputs that blocked the calc
};

export type GermanCalc = {
  inputs: {
    plotsize: number | null;
    gfa: number | null;
    gla: Resolved<number | null>;
    glaExpression: string;
    scr: number | null;
    fsr: number | null;
    commercialArea: number;
  };
  rates: {
    rate100: Resolved<number | null>;
    rate200: Resolved<number | null>;
    rate300: Resolved<number | null>;
    rate400: Resolved<number | null>;
    rate500: Resolved<number | null>;
    lump600: Resolved<number | null>;
    pct700: Resolved<number | null>;
    quote800: Resolved<number | null>;
    pctContingency: Resolved<number | null>;
    pctOhp: Resolved<number | null>;
  };
  kg: {
    kg100: KgResult;
    kg200: KgResult;
    kg300: KgResult;
    kg400: KgResult;
    kg500: KgResult;
    kg600: KgResult;
    kg700: KgResult;
    kg800: KgResult;
    contingencies: KgResult;
    ohp: KgResult;
  };
  total: number | null;
  partialTotal: number;
  missingKgLabels: string[];
  isPartial: boolean;
  /** the inflation multiplier applied to KG 200–600 (1 = none) */
  inflationFactor: number;
};

export const GERMAN_DEFAULTS = {
  rate100: null as number | null, // BRW — yearly land valuation lookup
  rate200: 30, // €/m² site clearance — illustrative
  rate300: null as number | null, // BKI / manufacturer — building construction
  rate400: null as number | null, // BKI / manufacturer — technical systems
  rate500: 60, // €/m² landscaping — illustrative
  lump600: 30000, // illustrative furnishing lump
  pct700: 0.1, // 10% incidentals
  quote800: null as number | null, // capital-provider quote
  pctContingency: 0.03,
  pctOhp: 0.055,
};

export const NOMINAL_SOURCE: Record<string, string> = {
  rate100: "BRW",
  rate200: "DEFAULT",
  rate300: "BKI",
  rate400: "BKI",
  rate500: "DEFAULT",
  lump600: "USER",
  pct700: "DEFAULT",
  quote800: "API",
  pctContingency: "DEFAULT",
  pctOhp: "DEFAULT",
};

export function computeGerman(
  arch: ArchitecturalMetricsSnapshot,
  wfl: WflOption,
  overrides: Record<string, number>,
  bki?: BkiInput | null,
  inflationFactor = 1
): GermanCalc {
  const plotsize = arch.siteAreaHectares != null ? arch.siteAreaHectares * 10000 : null;
  const gfa = arch.GEATotal ?? null;
  const scr = arch.LandCoverageTotal ?? null;
  const fsr =
    plotsize != null && plotsize > 0 && gfa != null ? gfa / plotsize : null;

  const gla = computeGla(arch, wfl, overrides);
  const glaExpression = glaExpressionFor(arch, wfl);
  const commercialArea = arch.CommercialAreaTotal ?? 0;

  // Inflation multiplies the BKI-rated construction groups (KG 200–600). KG 100
  // (land) and KG 800 (capital) are excluded — those are entered as current-day
  // figures. KG 700 / contingencies / OHP inherit it for free, since they're a
  // % of Σ(KG 200..500) which is already inflated. Default 1.0 = no change.
  const inflation = Number.isFinite(inflationFactor) && inflationFactor > 0 ? inflationFactor : 1;
  const inflSuffix = inflation === 1 ? "" : ` × ${inflation}`;

  // Resolve BKI city rates once if BKI is active.
  const bkiRates = bki ? applyBki(bki) : null;
  const bkiSource = bkiRates ? `BKI · ${bkiRates.resolved.city}` : "";

  const resolve = <K extends keyof typeof GERMAN_DEFAULTS>(key: K): Resolved<number | null> => {
    // Manual override always wins so a user can pin a value over BKI.
    if (overrides[key] != null) return { value: overrides[key], source: "MANUAL" };
    if (bkiRates) {
      if (key === "rate200") return { value: bkiRates.kg200, source: bkiSource };
      if (key === "rate300") return { value: bkiRates.kg300, source: bkiSource };
      if (key === "rate400") return { value: bkiRates.kg400, source: bkiSource };
      if (key === "rate500") return { value: bkiRates.kg500, source: bkiSource };
      // BKI gives KG 600 in €/m²; convert to a lump by multiplying by GLA.
      if (key === "lump600" && gla.value != null) {
        return { value: bkiRates.kg600 * gla.value, source: bkiSource };
      }
    }
    const def = GERMAN_DEFAULTS[key];
    if (def != null) return { value: def, source: "DEFAULT" };
    return { value: null, source: NOMINAL_SOURCE[key] ?? "—" };
  };

  const rates = {
    rate100: resolve("rate100"),
    rate200: resolve("rate200"),
    rate300: resolve("rate300"),
    rate400: resolve("rate400"),
    rate500: resolve("rate500"),
    lump600: resolve("lump600"),
    pct700: resolve("pct700"),
    quote800: resolve("quote800"),
    pctContingency: resolve("pctContingency"),
    pctOhp: resolve("pctOhp"),
  };

  const kg100: KgResult = makeKg(
    plotsize != null && rates.rate100.value != null ? plotsize * rates.rate100.value : null,
    `Plotsize × rate₁₀₀ = ${fmt(plotsize)} × ${fmtRate(rates.rate100.value)}`,
    [
      ...(plotsize == null ? ["Plotsize"] : []),
      ...(rates.rate100.value == null ? ["rate₁₀₀ (BRW)"] : []),
    ]
  );
  const kg200: KgResult = makeKg(
    plotsize != null && rates.rate200.value != null
      ? plotsize * rates.rate200.value * inflation
      : null,
    `Plotsize × rate₂₀₀${inflSuffix} = ${fmt(plotsize)} × ${fmtRate(rates.rate200.value)}${inflSuffix}`,
    [
      ...(plotsize == null ? ["Plotsize"] : []),
      ...(rates.rate200.value == null ? ["rate₂₀₀"] : []),
    ]
  );
  // KG 300/400 — the construction lines, calculated on the reference area
  // (BGF by default). When BKI supplies a commercial rate AND the building has
  // commercial floorspace, split: (refArea − commercial) at the residential
  // rate + commercial at the commercial rate. Otherwise the whole reference
  // area uses the single resolved rate. Inflation applies to the result.
  const constructionKg = (
    resiRate: Resolved<number | null>,
    commercialRate: number | null,
    rateSub: string
  ): KgResult => {
    const refArea = gla.value;
    if (refArea == null || resiRate.value == null) {
      return makeKg(null, `refArea × rate${rateSub}${inflSuffix}`, [
        ...(refArea == null ? ["reference area"] : []),
        ...(resiRate.value == null ? [`rate${rateSub} (BKI)`] : []),
      ]);
    }
    const split = commercialRate != null && commercialArea > 0;
    const resiArea = split ? Math.max(0, refArea - commercialArea) : refArea;
    const base = split
      ? resiArea * resiRate.value + commercialArea * commercialRate
      : refArea * resiRate.value;
    const formula = split
      ? `[(${fmt(refArea)} − ${fmt(commercialArea)}) × ${fmtRate(resiRate.value)} + ${fmt(
          commercialArea
        )} × ${fmtRate(commercialRate)}]${inflSuffix}`
      : `${fmt(refArea)} × ${fmtRate(resiRate.value)}${inflSuffix}`;
    return makeKg(base * inflation, formula, []);
  };
  const kg300: KgResult = constructionKg(rates.rate300, bkiRates?.commercial.kg300 ?? null, "₃₀₀");
  const kg400: KgResult = constructionKg(rates.rate400, bkiRates?.commercial.kg400 ?? null, "₄₀₀");
  const kg500: KgResult = makeKg(
    plotsize != null && scr != null && rates.rate500.value != null
      ? plotsize * (1 - scr) * rates.rate500.value * inflation
      : null,
    `Plotsize × (1 − SCR) × rate₅₀₀${inflSuffix} = ${fmt(plotsize)} × ${fmt(
      scr == null ? null : 1 - scr,
      3
    )} × ${fmtRate(rates.rate500.value)}${inflSuffix}`,
    [
      ...(plotsize == null ? ["Plotsize"] : []),
      ...(scr == null ? ["SCR"] : []),
      ...(rates.rate500.value == null ? ["rate₅₀₀"] : []),
    ]
  );
  const kg600: KgResult = makeKg(
    rates.lump600.value != null ? rates.lump600.value * inflation : null,
    `lump₆₀₀${inflSuffix} = ${fmtRate(rates.lump600.value)}${inflSuffix}`,
    rates.lump600.value == null ? ["lump₆₀₀"] : []
  );

  const sum200to500 =
    kg200.value != null && kg300.value != null && kg400.value != null && kg500.value != null
      ? kg200.value + kg300.value + kg400.value + kg500.value
      : null;

  const kg700: KgResult = makeKg(
    sum200to500 != null && rates.pct700.value != null ? sum200to500 * rates.pct700.value : null,
    `${pct(rates.pct700.value)} × Σ(KG 200..500) = ${pct(rates.pct700.value)} × ${fmt(sum200to500)}`,
    [
      ...(sum200to500 == null ? ["Σ(KG 200..500)"] : []),
      ...(rates.pct700.value == null ? ["%₇₀₀"] : []),
    ]
  );
  const kg800: KgResult = makeKg(
    rates.quote800.value,
    `quote₈₀₀ = ${fmtRate(rates.quote800.value)}`,
    rates.quote800.value == null ? ["quote₈₀₀"] : []
  );
  const contingencies: KgResult = makeKg(
    sum200to500 != null && rates.pctContingency.value != null
      ? sum200to500 * rates.pctContingency.value
      : null,
    `${pct(rates.pctContingency.value)} × Σ(KG 200..500)`,
    [
      ...(sum200to500 == null ? ["Σ(KG 200..500)"] : []),
      ...(rates.pctContingency.value == null ? ["%cont"] : []),
    ]
  );
  const ohp: KgResult = makeKg(
    sum200to500 != null && rates.pctOhp.value != null ? sum200to500 * rates.pctOhp.value : null,
    `${pct(rates.pctOhp.value)} × Σ(KG 200..500)`,
    [
      ...(sum200to500 == null ? ["Σ(KG 200..500)"] : []),
      ...(rates.pctOhp.value == null ? ["%ohp"] : []),
    ]
  );

  const labelledKgs: Array<[string, KgResult]> = [
    ["KG 100", kg100],
    ["KG 200", kg200],
    ["KG 300", kg300],
    ["KG 400", kg400],
    ["KG 500", kg500],
    ["KG 600", kg600],
    ["KG 700", kg700],
    ["KG 800", kg800],
    ["Contingencies", contingencies],
    ["OHP", ohp],
  ];
  const isPartial = labelledKgs.some(([, k]) => k.value == null);
  const partialTotal = labelledKgs.reduce((acc, [, k]) => acc + (k.value ?? 0), 0);
  const total = isPartial ? null : partialTotal;
  const missingKgLabels = labelledKgs.filter(([, k]) => k.value == null).map(([l]) => l);

  return {
    inputs: { plotsize, gfa, gla, glaExpression, scr, fsr, commercialArea },
    rates,
    kg: {
      kg100,
      kg200,
      kg300,
      kg400,
      kg500,
      kg600,
      kg700,
      kg800,
      contingencies,
      ohp,
    },
    total,
    partialTotal,
    missingKgLabels,
    isPartial,
    inflationFactor: inflation,
  };

  function makeKg(value: number | null, formula: string, missing: string[]): KgResult {
    return { value, formula, missing };
  }
}

export function computeGla(
  arch: ArchitecturalMetricsSnapshot,
  wfl: WflOption,
  overrides: Record<string, number>
): Resolved<number | null> {
  if (overrides.gla != null) return { value: overrides.gla, source: "MANUAL" };
  switch (wfl) {
    case "bgf":
      // BGF / GEA is a real Tessa field — no derivation, no WoFlV caveat.
      return { value: arch.GEATotal ?? null, source: "HAVE · GEA" };
    case "strict": {
      if (arch.GIATotal == null) return { value: null, source: "DERIVED · strict" };
      const v =
        arch.GIATotal -
        ((arch.CoreAndCorridorAreaTotal ?? 0) +
          (arch.ServiceAreaTotal ?? 0) +
          (arch.AmenitiesAreaTotal ?? 0));
      return { value: v, source: "DERIVED · strict" };
    }
    case "nia":
      return { value: arch.NIATotal ?? null, source: "DERIVED · NIA" };
    case "leasable":
      if (arch.NIATotal == null) return { value: null, source: "DERIVED · leasable" };
      return {
        value:
          arch.NIATotal +
          (arch.CommercialAreaTotal ?? 0) +
          (arch.NonResidentialAreaTotal ?? 0),
        source: "DERIVED · leasable",
      };
  }
}

export function glaExpressionFor(arch: ArchitecturalMetricsSnapshot, wfl: WflOption): string {
  switch (wfl) {
    case "bgf":
      return `BGF/GEA(${fmt(arch.GEATotal)})`;
    case "strict":
      return `GIA(${fmt(arch.GIATotal)}) − cores(${fmt(
        arch.CoreAndCorridorAreaTotal
      )}) − services(${fmt(arch.ServiceAreaTotal)}) − amenities(${fmt(arch.AmenitiesAreaTotal)})`;
    case "nia":
      return `NIA(${fmt(arch.NIATotal)})`;
    case "leasable":
      return `NIA(${fmt(arch.NIATotal)}) + commercial(${fmt(
        arch.CommercialAreaTotal
      )}) + non-residential(${fmt(arch.NonResidentialAreaTotal)})`;
  }
}

// ─── Display formatters (used in formula strings) ──────────────────────────

export function fmt(n: number | null | undefined, decimals = 0): string {
  if (n == null || Number.isNaN(n)) return "—";
  return n.toLocaleString("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

export function fmtRate(n: number | null | undefined): string {
  return n == null ? "?" : String(Math.round(n * 100) / 100);
}

export function pct(n: number | null | undefined): string {
  if (n == null) return "?%";
  return `${(n * 100).toFixed(1)}%`;
}
